import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Building2, Copy, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESET_COLORS = ["#FFB000", "#0EA5E9", "#22C55E", "#A855F7", "#EF4444", "#F97316", "#06B6D4", "#EC4899"];

export default function ClientNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    accent_color: "#FFB000",
    logo_url: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    industry: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact_name || !form.contact_email) {
      toast.error("Name, contact name, and contact email are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/clients", form);
      setCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create client");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  // ─── Success state ───
  if (created) {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="client-created">
        <button
          onClick={() => navigate("/clients")}
          className="flex items-center gap-2 text-sm text-white/55 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> All clients
        </button>
        <div className="bg-[#121214] border border-emerald-500/30 rounded-2xl p-8">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 border-2 border-emerald-500 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7 text-emerald-400" strokeWidth={2} />
          </div>
          <h2 className="text-2xl font-bold mb-2">{created.name} is live</h2>
          <p className="text-white/55 text-sm mb-6">Portal created and welcome email {created.email_sent ? "sent" : "queued"} to {created.contact_email}.</p>

          <div className="space-y-3">
            <div className="bg-black/30 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-1.5">Portal URL</div>
              <div className="flex items-center gap-2">
                <code className="text-sm text-[#FFB000] flex-1 truncate font-mono" data-testid="client-portal-url">{created.portal_url}</code>
                <button onClick={() => copy(created.portal_url, "URL")} className="text-white/50 hover:text-white" data-testid="copy-portal-url">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="bg-black/30 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-1.5">Temporary password (shown once)</div>
              <div className="flex items-center gap-2">
                <code className="text-sm text-[#FFB000] flex-1 font-mono" data-testid="client-temp-password">{created.temp_password}</code>
                <button onClick={() => copy(created.temp_password, "Password")} className="text-white/50 hover:text-white" data-testid="copy-temp-password">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[11px] text-amber-300/70 mt-2">⚠ Copy this now — it can't be retrieved later (use Reset Password if you lose it).</div>
            </div>
          </div>

          <div className="flex gap-3 mt-7">
            <button
              onClick={() => navigate(`/clients/${created.id}`)}
              className="flex-1 px-5 py-2.5 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633]"
              data-testid="view-client-btn"
            >
              View client →
            </button>
            <button
              onClick={() => { setCreated(null); setForm({ name: "", accent_color: "#FFB000", logo_url: "", contact_name: "", contact_email: "", contact_phone: "", industry: "", notes: "" }); }}
              className="px-5 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white border border-white/10 hover:border-white/25"
            >
              Add another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Form ───
  return (
    <div className="space-y-6 max-w-2xl" data-testid="client-new-page">
      <button
        onClick={() => navigate("/clients")}
        className="flex items-center gap-2 text-sm text-white/55 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> All clients
      </button>
      <div>
        <h1 className="text-3xl font-bold tracking-tight">New client</h1>
        <p className="text-sm text-white/50 mt-2">Fill this in. The system creates the portal, generates a password, and emails the client.</p>
      </div>

      <form onSubmit={submit} className="space-y-6 bg-[#121214] border border-white/5 rounded-2xl p-7">
        {/* Section: Business */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB000] font-medium mb-3 flex items-center gap-2">
            <Building2 className="w-3 h-3" /> Business
          </div>
          <div className="space-y-3">
            <Field label="Company name" required>
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Acme Plumbing Co"
                data-testid="client-name-input"
                className="input-field"
                autoFocus
              />
            </Field>
            <Field label="Industry">
              <input
                value={form.industry}
                onChange={(e) => set("industry", e.target.value)}
                placeholder="e.g. HVAC · Plumbing · Dental"
                data-testid="client-industry-input"
                className="input-field"
              />
            </Field>
          </div>
        </div>

        {/* Section: Branding */}
        <div className="pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB000] font-medium mb-3">Branding (shown to client)</div>
          <div className="space-y-3">
            <Field label="Accent color">
              <div className="flex items-center gap-3 flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set("accent_color", c)}
                    aria-label={c}
                    className={`w-8 h-8 rounded-lg transition-all ${form.accent_color === c ? "ring-2 ring-white/60 scale-110" : "ring-1 ring-white/10"}`}
                    style={{ background: c }}
                  />
                ))}
                <input
                  type="text"
                  value={form.accent_color}
                  onChange={(e) => set("accent_color", e.target.value)}
                  className="input-field !w-32 font-mono text-xs"
                  data-testid="client-color-input"
                />
              </div>
            </Field>
            <Field label="Logo URL" hint="Public URL to a PNG/SVG (paste from your CDN, or use placeholders for now)">
              <input
                value={form.logo_url}
                onChange={(e) => set("logo_url", e.target.value)}
                placeholder="https://cdn.example.com/acme-logo.png"
                data-testid="client-logo-url-input"
                className="input-field"
              />
            </Field>
          </div>
        </div>

        {/* Section: Contact */}
        <div className="pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB000] font-medium mb-3">Primary contact (gets portal access)</div>
          <div className="space-y-3">
            <Field label="Full name" required>
              <input
                value={form.contact_name}
                onChange={(e) => set("contact_name", e.target.value)}
                placeholder="e.g. Sarah Acme"
                data-testid="client-contact-name-input"
                className="input-field"
              />
            </Field>
            <Field label="Email" required hint="Used as login + welcome email recipient">
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                placeholder="sarah@acmeplumbing.com"
                data-testid="client-contact-email-input"
                className="input-field"
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => set("contact_phone", e.target.value)}
                placeholder="+1 (312) 555-1234"
                data-testid="client-contact-phone-input"
                className="input-field"
              />
            </Field>
          </div>
        </div>

        {/* Section: Notes */}
        <div className="pt-4 border-t border-white/5">
          <Field label="Internal notes" hint="Only Patrick sees this">
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
              placeholder="What's their setup? Existing tools? Pain points?"
              data-testid="client-notes-input"
              className="input-field"
            />
          </Field>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            data-testid="create-client-submit"
            className="px-6 py-2.5 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create client + send welcome"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className="px-5 py-2.5 rounded-lg text-sm text-white/55 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </form>

      <style>{`
        .input-field { width: 100%; padding: 10px 13px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 9px; color: #fff; font-size: 14px; outline: none; transition: border-color .2s; font-family: 'Inter', sans-serif; }
        .input-field:focus { border-color: #FFB000; background: rgba(255,176,0,0.04); }
        .input-field::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-white/65 mb-1.5">
        {label} {required && <span className="text-[#FFB000]">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-white/35 mt-1">{hint}</div>}
    </label>
  );
}
